import React, { useState, useEffect, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faPlus, faSave, faSpinner, faToggleOn, faToggleOff } from '@fortawesome/free-solid-svg-icons';
import { WorkflowService, sharedApiClient } from '../../fetcher';
import { IdentityService, TeamService, CustomDropdown, useNotifications } from '@medprompt/shared';
import WorkflowConditionCard from './WorkflowConditionCard';
import { DEFAULT_NEW_CONDITION, getWorkflowConditionsValidationError } from '../WorkflowConditionEnum';
import { DEFAULT_NEW_ACTION } from '../WorkflowActionEnum';
import WorkflowActionCard from './WorkflowActionCard';
import WorkflowDiagram from './WorkflowDiagram';
import '@medprompt/shared/src/common/modals.css';
import '../WorkflowView.css';

// Event type options
const EVENT_TYPE_OPTIONS = [
  { value: 'DOCUMENT_COMPLETED', name: 'Document Completed' }
];

const CreateWorkflowModal = ({ isOpen, onClose, onSuccess }) => {
  const { addNotification } = useNotifications();
  const workflowManager = useMemo(() => new WorkflowService(sharedApiClient), []);

  // Form state
  const [workflowName, setWorkflowName] = useState('');
  const [workflowEvent, setWorkflowEvent] = useState('DOCUMENT_COMPLETED');
  const [workflowActive, setWorkflowActive] = useState(true);
  const [conditions, setConditions] = useState([]);
  const [actions, setActions] = useState([]);
  const [saving, setSaving] = useState(false);

  // Users and teams
  const [activeUsers, setActiveUsers] = useState([]);
  const [teams, setTeams] = useState([]);

  // Event dropdown state
  const [eventDropdownOpen, setEventDropdownOpen] = useState(false);

  // Title editing state
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const titleInputRef = React.useRef(null);

  // Session storage key for auto-save
  const STORAGE_KEY = 'medprompt_workflow_create_draft';

  // Fetch users and teams
  useEffect(() => {
    if (!isOpen) return;

    const identityManager = new IdentityService();
    const teamManager = new TeamService();
    
    identityManager.fetchActiveClinicUsers()
      .then((users) => setActiveUsers(users || []))
      .catch((error) => {
        console.error('Failed to fetch active users:', error);
        setActiveUsers([]);
      });
    
    teamManager.fetchTeams()
      .then((teamsData) => setTeams(teamsData || []))
      .catch((error) => {
        console.error('Failed to fetch teams:', error);
        setTeams([]);
      });
  }, [isOpen]);

  // Load draft from session storage when modal opens, reset when closes
  useEffect(() => {
    if (isOpen) {
      // Load draft from session storage
      try {
        const saved = sessionStorage.getItem(STORAGE_KEY);
        if (saved) {
          const draft = JSON.parse(saved);
          if (draft.name !== undefined) setWorkflowName(draft.name);
          if (draft.isActive !== undefined) setWorkflowActive(draft.isActive);
          if (draft.conditions) setConditions(draft.conditions);
          if (draft.actions) setActions(draft.actions);
        }
      } catch (error) {
        console.error('Failed to load draft from session storage:', error);
      }
    } else {
      // Reset form when modal closes
      setWorkflowName('');
      setWorkflowEvent('DOCUMENT_COMPLETED');
      setWorkflowActive(true);
      setConditions([]);
      setActions([]);
      setIsEditingTitle(false);
    }
  }, [isOpen]);

  // Auto-save draft to session storage whenever form state changes
  useEffect(() => {
    if (!isOpen) return;

    try {
      const draft = {
        name: workflowName,
        isActive: workflowActive,
        conditions: conditions,
        actions: actions
      };
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(draft));
    } catch (error) {
      console.error('Failed to save draft to session storage:', error);
    }
  }, [workflowName, workflowActive, conditions, actions, isOpen]);

  // Focus input when editing starts
  useEffect(() => {
    if (isEditingTitle && titleInputRef.current) {
      titleInputRef.current.focus();
    }
  }, [isEditingTitle]);

  // Title editing handlers
  const handleTitleClick = () => {
    setIsEditingTitle(true);
  };

  const handleTitleBlur = () => {
    setIsEditingTitle(false);
  };

  const handleTitleKeyDown = (e) => {
    if (e.key === 'Escape') {
      setWorkflowName('');
      setIsEditingTitle(false);
    } else if (e.key === 'Enter') {
      setIsEditingTitle(false);
    }
  };

  // Condition handlers
  const handleAddCondition = () => {
    setConditions([...conditions, { ...DEFAULT_NEW_CONDITION }]);
  };

  const handleUpdateCondition = (index, newCondition) => {
    const newConditions = [...conditions];
    newConditions[index] = newCondition;
    setConditions(newConditions);
  };

  const handleRemoveCondition = (index) => {
    setConditions(conditions.filter((_, i) => i !== index));
  };

  // Action handlers
  const handleAddAction = () => {
    setActions([...actions, { ...DEFAULT_NEW_ACTION }]);
  };

  const handleUpdateAction = (index, newAction) => {
    const newActions = [...actions];
    newActions[index] = newAction;
    setActions(newActions);
  };

  const handleRemoveAction = (index) => {
    setActions(actions.filter((_, i) => i !== index));
  };

  // Handle save
  const handleSave = async () => {
    if (!workflowName.trim()) {
      addNotification('save-validation-error', 'Workflow name is required', { isError: true });
      return;
    }

    if (actions.length === 0) {
      addNotification('save-validation-error', 'At least one action is required', { isError: true });
      return;
    }

    const conditionsError = getWorkflowConditionsValidationError(conditions);
    if (conditionsError) {
      addNotification('save-validation-error', conditionsError, { isError: true });
      return;
    }

    try {
      setSaving(true);

      const request = {
        name: workflowName.trim(),
        event: workflowEvent,
        conditions: conditions,
        actions: actions
      };

      await workflowManager.createWorkflowRule(request);
      addNotification('workflow-created', 'Workflow created successfully');
      
      // Clear draft from session storage after successful creation
      try {
        sessionStorage.removeItem(STORAGE_KEY);
      } catch (error) {
        console.error('Failed to clear draft from session storage:', error);
      }
      
      if (onSuccess) {
        onSuccess();
      }
      
      onClose();
    } catch (error) {
      console.error('Failed to save workflow:', error);
      addNotification('save-workflow-error', 'Failed to save workflow', { isError: true });
    } finally {
      setSaving(false);
    }
  };

  // Current workflow for diagram
  const currentWorkflow = useMemo(() => ({
    name: workflowName,
    event: workflowEvent,
    conditions: conditions,
    actions: actions,
    isActive: workflowActive
  }), [workflowName, workflowEvent, conditions, actions, workflowActive]);

  const selectedEventType = EVENT_TYPE_OPTIONS.find(opt => opt.value === workflowEvent) || EVENT_TYPE_OPTIONS[0];

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" style={{ zIndex: 1001 }}>
      <div className="modal-container modal-animated create-workflow-modal">
        <div className="modal-header">
          <h2>Create New Document Workflow</h2>
          <button className="close-button" onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <div className="modal-content create-workflow-content">
          {/* Left Panel - Diagram */}
          <div className="create-workflow-panel create-workflow-diagram-panel">
            <h3>Workflow Preview</h3>
            <div className="create-workflow-diagram-container">
              <WorkflowDiagram workflow={currentWorkflow} activeUsers={activeUsers} teams={teams} />
            </div>
          </div>

          {/* Right Panel - Builder */}
          <div className="create-workflow-panel create-workflow-builder-panel">
            <h3>Configure Document Workflow</h3>
            
            <div className="create-workflow-form">
              {/* Workflow Name */}
              <div className="workflow-title-section">
                <label>Workflow Name:</label>
                {isEditingTitle ? (
                  <input
                    ref={titleInputRef}
                    type="text"
                    className="workflow-title-input"
                    value={workflowName}
                    onChange={(e) => setWorkflowName(e.target.value)}
                    onBlur={handleTitleBlur}
                    onKeyDown={handleTitleKeyDown}
                    placeholder="Add title here"
                  />
                ) : (
                  <div
                    className="workflow-title-text workflow-title-editable"
                    onClick={handleTitleClick}
                    title="Click to edit"
                  >
                    {workflowName || <em style={{ color: '#999' }}>Add title here</em>}
                  </div>
                )}
              </div>

              {/* Status */}
              <div className="config-row">
                <label>Status:</label>
                <button
                  className={`status-toggle ${workflowActive ? 'active' : 'inactive'}`}
                  onClick={() => setWorkflowActive(!workflowActive)}
                  type="button"
                >
                  <FontAwesomeIcon icon={workflowActive ? faToggleOn : faToggleOff} />
                  {workflowActive ? 'Active' : 'Inactive'}
                </button>
              </div>

              {/* Event Type */}
              <div className="config-row">
                <label>Event Type:</label>
                <span style={{ 
                  fontFamily: 'Quicksand, sans-serif', 
                  color: '#333',
                  fontSize: '15px'
                }}>
                  When a document completes processing
                </span>
              </div>

              {/* Conditions Section */}
              <div className="workflow-builder-section">
                <div className="section-header">
                  <h4>Conditions</h4>
                  <button
                    className="add-button"
                    onClick={handleAddCondition}
                    type="button"
                  >
                    <FontAwesomeIcon icon={faPlus} /> Add Condition
                  </button>
                </div>
                <div className="cards-container">
                  {conditions.length === 0 ? (
                    <div className="empty-state">
                      <p>No conditions defined. Add a condition to filter when this workflow runs.</p>
                    </div>
                  ) : (
                    conditions.map((condition, index) => (
                      <WorkflowConditionCard
                        key={index}
                        condition={condition}
                        index={index}
                        onChange={(newCondition) => handleUpdateCondition(index, newCondition)}
                        onRemove={handleRemoveCondition}
                        activeUsers={activeUsers}
                        teams={teams}
                      />
                    ))
                  )}
                </div>
              </div>

              {/* Actions Section */}
              <div className="workflow-builder-section">
                <div className="section-header">
                  <h4>Actions</h4>
                  <button
                    className="add-button"
                    onClick={handleAddAction}
                    type="button"
                  >
                    <FontAwesomeIcon icon={faPlus} /> Add Action
                  </button>
                </div>
                <div className="cards-container">
                  {actions.length === 0 ? (
                    <div className="empty-state">
                      <p>No actions defined. Add at least one action to execute when conditions are met.</p>
                    </div>
                  ) : (
                    actions.map((action, index) => (
                      <WorkflowActionCard
                        key={index}
                        action={action}
                        index={index}
                        onChange={(newAction) => handleUpdateAction(index, newAction)}
                        onRemove={handleRemoveAction}
                        activeUsers={activeUsers}
                        teams={teams}
                      />
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="modal-actions">
          <button className="cancel-button" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button
            className="header-button header-button-primary"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? (
              <>
                <FontAwesomeIcon icon={faSpinner} spin /> Creating...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faSave} /> Create Workflow
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateWorkflowModal;
